import { getJson } from "../net/http.js";
import { el } from "../ui/dom.js";
import { toggleFullscreen } from "../ui/fullscreen.js";
import { ArchivePlayer, type PlayState } from "./player.js";
import { Timeline, clock, parseSpans, stamp, type SpanIn } from "./timeline.js";
import {
  type Retention,
  type RetentionIn,
  beyondRetention,
  parseRetention,
} from "./retention.js";

interface ArchiveCamera {
  name: string;
  display_name: string;
  /** Заведена ли камера в конфиге сейчас. */
  in_registry: boolean;
}

interface Extent {
  first: string | null;
  last: string | null;
}

/** Как часто перечитывать полосу сегодняшних суток: запись растёт. */
const TODAY_REFRESH_MS = 30_000;

/** Местная полночь дня, в котором лежит `ms`. */
function midnight(ms: number): number {
  const d = new Date(ms);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** Полночь следующих суток. Через `Date`, а не `+24ч`: в сутках перевода часов их 23 или 25. */
function nextMidnight(dayStart: number): number {
  const d = new Date(dayStart);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime();
}

function prevMidnight(dayStart: number): number {
  const d = new Date(dayStart);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1).getTime();
}

/** `YYYY-MM-DD` местных суток — формат `<input type="date">` и адреса. */
function dayKey(dayStart: number): string {
  const d = new Date(dayStart);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function parseDayKey(key: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).getTime();
}

/**
 * Вкладка архива: выбор камеры и суток, полоса, проигрыватель и управление.
 *
 * Сутки — единица просмотра. Прежняя полоса показывала «последние 24 часа от
 * сейчас», и всё, что старше, было недостижимо из интерфейса, хотя хранилось.
 * Выбранные сутки и камера живут в адресе (`#archive/камера/2026-10-14`):
 * ссылку можно переслать, и перезагрузка не теряет место.
 */
export class ArchiveView {
  private readonly picker = el<HTMLSelectElement>("archive-camera");
  private readonly dayInput = el<HTMLInputElement>("day-pick");
  private readonly status = el("archive-status");
  private readonly osd = el("archive-osd");
  private readonly label = el("archive-label");
  private readonly playButton = el<HTMLButtonElement>("archive-play");
  private readonly download = el<HTMLAnchorElement>("archive-download");
  private readonly stage = el("archive-stage");
  private readonly timeline: Timeline;
  private retention: Retention | null = null;
  private readonly player: ArchivePlayer;

  private camera = "";
  private dayStart = midnight(Date.now());
  private firstDay: number | null = null;
  private refresh: number | null = null;
  private visible = false;
  private loadToken = 0;
  /** Последнее состояние проигрывателя: подсказку «щёлкните» не показываем поверх сообщений. */
  private playState: PlayState["kind"] = "idle";
  /** Прошлое время кадра: по нему видно, что запись сама перешла полночь. */
  private lastFrame: number | null = null;

  constructor() {
    this.timeline = new Timeline(
      el<HTMLCanvasElement>("archive-timeline"),
      el<HTMLCanvasElement>("archive-overview"),
      el("timeline-tip"),
    );
    this.timeline.onPick = (ms) => this.pick(ms);

    const video = el<HTMLVideoElement>("archive-video");
    this.player = new ArchivePlayer(video, {
      state: (state) => this.showState(state),
      time: (ms) => this.showTime(ms),
      skipped: (asked, shown) =>
        this.say(`В ${clock(asked)} записи нет — показываю с ${clock(shown)}`),
      segment: (id) => {
        this.download.hidden = id === null;
        if (id !== null) this.download.href = `/api/archive/segment/${id}/download`;
      },
    });
    video.addEventListener("click", () => this.player.toggle());

    this.picker.addEventListener("change", () => {
      this.camera = this.picker.value;
      this.player.use(this.camera);
      void this.loadExtent();
      void this.loadDay(this.dayStart);
    });
    this.dayInput.addEventListener("change", () => {
      const day = parseDayKey(this.dayInput.value);
      if (day !== null) void this.loadDay(day);
    });
    el("day-prev").addEventListener("click", () => void this.loadDay(prevMidnight(this.dayStart)));
    el("day-next").addEventListener("click", () => void this.loadDay(nextMidnight(this.dayStart)));
    el("day-today").addEventListener("click", () => void this.loadDay(midnight(Date.now())));
    el("zoom-reset").addEventListener("click", () => this.timeline.resetZoom());

    this.playButton.addEventListener("click", () => this.player.toggle());
    for (const button of document.querySelectorAll<HTMLButtonElement>("[data-step]")) {
      button.addEventListener("click", () => this.player.step(Number(button.dataset.step)));
    }
    el<HTMLSelectElement>("archive-rate").addEventListener("change", (e) => {
      this.player.setRate(Number((e.target as HTMLSelectElement).value));
    });
    el("archive-full").addEventListener("click", () => toggleFullscreen(this.stage));
    document.addEventListener("keydown", (e) => this.key(e));
  }

  /** Вкладка открыта. `hash` — место из адреса, если пришли по ссылке. */
  async show(hash: string | null): Promise<void> {
    this.visible = true;
    await this.fillCameraPicker();

    const [, rawCamera, day] = (hash ?? "").split("/");
    // Имя камеры в адресе закодировано (`loadDay`): без раскодирования
    // кириллица или пробел не совпали бы ни с одной камерой, и ссылка молча
    // открыла бы первую.
    let camera: string | undefined;
    try {
      camera = rawCamera ? decodeURIComponent(rawCamera) : undefined;
    } catch {
      camera = undefined;
    }
    if (camera && [...this.picker.options].some((o) => o.value === camera)) {
      this.picker.value = camera;
    }
    const fromHash = day ? parseDayKey(day) : null;
    if (fromHash !== null) this.dayStart = fromHash;

    this.camera = this.picker.value;
    this.player.use(this.camera);
    if (this.camera === "") {
      this.say("записей пока нет");
      return;
    }
    void this.loadExtent();
    await this.loadDay(this.dayStart);
    this.refresh ??= window.setInterval(() => void this.refreshToday(), TODAY_REFRESH_MS);
  }

  /** Вкладка закрыта: проигрыватель отпускает сеть. */
  hide(): void {
    this.visible = false;
    this.player.stop();
    if (this.refresh !== null) window.clearInterval(this.refresh);
    this.refresh = null;
  }

  /**
   * Список для архива запрашивается ОТДЕЛЬНО и намеренно.
   *
   * `/api/cameras` перечисляет то, куда можно публиковать сейчас, и выключенных
   * камер там нет. Но записи снятой с эксплуатации камеры остаются, и смотреть
   * их по-прежнему нужно. Список по живым камерам молча лишал бы её доступа к
   * собственному архиву.
   */
  private async fillCameraPicker(): Promise<void> {
    const chosen = this.picker.value;
    let cameras: ArchiveCamera[];
    try {
      cameras = (await getJson<{ cameras: ArchiveCamera[] }>("/api/archive/cameras")).cameras;
    } catch (err) {
      console.warn("не получить список камер архива", err);
      return;
    }

    this.picker.innerHTML = "";
    for (const camera of cameras) {
      const option = document.createElement("option");
      option.value = camera.name;
      // Снятую с эксплуатации помечаем: иначе непонятно, почему запись
      // кончается на полуслове.
      option.textContent =
        (camera.display_name || camera.name) +
        (camera.in_registry ? "" : " (снята с эксплуатации)");
      this.picker.appendChild(option);
    }
    if (chosen !== "" && cameras.some((c) => c.name === chosen)) {
      this.picker.value = chosen;
    }
  }

  /** Границы архива: от них зависят крайние даты в выборе суток. */
  private async loadExtent(): Promise<void> {
    const camera = this.camera;
    try {
      const extent = await getJson<Extent>(
        `/api/archive/${encodeURIComponent(camera)}/extent`,
      );
      if (camera !== this.camera) return;
      this.firstDay = extent.first ? midnight(Date.parse(extent.first)) : null;
      el("archive-extent").textContent = extent.first
        ? `архив с ${new Date(Date.parse(extent.first)).toLocaleDateString("ru-RU", {
            day: "numeric",
            month: "long",
          })}`
        : "";
    } catch {
      this.firstDay = null;
    }
    this.syncDayControls();
  }

  private async loadDay(dayStart: number): Promise<void> {
    const token = ++this.loadToken;
    this.dayStart = dayStart;
    this.syncDayControls();
    history.replaceState(
      null,
      "",
      `#archive/${encodeURIComponent(this.camera)}/${dayKey(dayStart)}`,
    );

    const spans = await this.fetchSpans(dayStart);
    if (token !== this.loadToken) return;
    this.timeline.setDay(dayStart, nextMidnight(dayStart), spans);
    // Конец записи мог прийти раньше новой полосы — тогда «следующая запись»
    // искалась по старым суткам. Повторяем теперь, когда полоса свежая.
    if (this.playState === "ended") this.showState({ kind: "ended" });
    if (this.playState === "idle") {
      this.say(
        spans.length === 0
          ? "За эти сутки записи нет"
          : "Щёлкните по полосе, чтобы смотреть с этого момента",
      );
    }
  }

  private async refreshToday(): Promise<void> {
    if (!this.visible || this.dayStart !== midnight(Date.now())) return;
    const token = this.loadToken;
    const spans = await this.fetchSpans(this.dayStart);
    if (token === this.loadToken) this.timeline.setSpans(spans);
  }

  private async fetchSpans(dayStart: number) {
    const from = new Date(dayStart).toISOString();
    const to = new Date(nextMidnight(dayStart)).toISOString();
    try {
      const body = await getJson<{ spans: SpanIn[]; retention?: RetentionIn | null }>(
        `/api/archive/${encodeURIComponent(this.camera)}/timeline` +
          `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`,
      );
      this.retention = parseRetention(body.retention);
      this.timeline.setRetention(this.retention);
      return parseSpans(body.spans);
    } catch (err) {
      console.warn("не загрузить полосу", err);
      this.say("Не удалось загрузить полосу записи");
      return [];
    }
  }

  private syncDayControls(): void {
    const today = midnight(Date.now());
    this.dayInput.value = dayKey(this.dayStart);
    this.dayInput.max = dayKey(today);
    if (this.firstDay !== null) this.dayInput.min = dayKey(this.firstDay);
    el<HTMLButtonElement>("day-next").disabled = this.dayStart >= today;
    el<HTMLButtonElement>("day-prev").disabled =
      this.firstDay !== null && this.dayStart <= this.firstDay;
    el<HTMLButtonElement>("day-today").disabled = this.dayStart === today;
  }

  /** Щелчок по полосе. По дыре не молчим: говорим и предлагаем ближайшую запись. */
  private pick(ms: number): void {
    if (ms > Date.now()) {
      this.say(`${clock(ms)} ещё не наступило`);
      return;
    }
    if (this.timeline.spanAt(ms)) {
      this.player.seek(ms);
      return;
    }
    const next = this.timeline.nextSpanAfter(ms);
    // За границей срока причина другая, и сказать надо её.
    this.say(
      this.retention && this.timeline.beyondRetention(ms)
        ? `${clock(ms)} — ${beyondRetention(this.retention.days)}`
        : `В ${clock(ms)} записи нет`,
    );
    if (next) this.offer(`Ближайшая запись — ${clock(next.start)}`, next.start);
  }

  /** Кнопка «перейти» под сообщением в окне видео. */
  private offer(text: string, ms: number): void {
    const go = document.createElement("button");
    go.type = "button";
    go.textContent = text;
    go.addEventListener("click", () => this.player.seek(ms));
    this.status.append(document.createElement("br"), go);
  }

  private showState(state: PlayState): void {
    this.playState = state.kind;
    this.playButton.textContent = state.kind === "playing" || state.kind === "buffering" ? "⏸" : "▶";
    switch (state.kind) {
      case "loading":
        this.say(
          state.slow
            ? "Готовлю запись из архива — первый просмотр давней записи может занять до минуты"
            : "Загрузка…",
          true,
        );
        break;
      case "buffering":
        this.say("Подгружается…", true);
        break;
      case "playing":
      case "paused":
        this.status.hidden = true;
        break;
      case "ended": {
        // Окно плейлиста — два часа; запись после долгой дыры в него не
        // попадает, но она есть, и сказать «записи нет» было бы неправдой.
        const next = this.lastFrame !== null ? this.timeline.nextSpanAfter(this.lastFrame) : null;
        this.say(next ? "Здесь запись прерывается" : "Дальше записи нет");
        if (next) this.offer(`Следующая запись — ${clock(next.start)}`, next.start);
        break;
      }
      case "error":
        this.say(`Не удалось: ${state.text}`);
        break;
      case "idle":
        this.osd.hidden = true;
        this.label.textContent = "";
        break;
    }
  }

  private showTime(ms: number | null): void {
    this.timeline.setPlayhead(ms);
    if (ms === null) {
      this.lastFrame = null;
      this.osd.hidden = true;
      return;
    }
    this.osd.hidden = false;
    this.osd.textContent = stamp(ms);
    this.label.textContent = stamp(ms);

    // Запись сама перешла через полночь — сутки на полосе переключаются
    // следом. Именно «перешла»: если человек листает другие сутки, пока
    // играет запись, возвращать его назад нельзя.
    const previous = this.lastFrame;
    this.lastFrame = ms;
    const dayEnd = nextMidnight(this.dayStart);
    if (previous !== null && previous >= this.dayStart && previous < dayEnd && ms >= dayEnd) {
      void this.loadDay(midnight(ms));
    }
  }

  private say(text: string, spinner = false): void {
    this.status.hidden = false;
    this.status.textContent = text;
    this.status.classList.toggle("stage-status-busy", spinner);
  }

  private key(e: KeyboardEvent): void {
    if (!this.visible) return;
    const target = e.target as HTMLElement | null;
    if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
    const big = e.shiftKey ? 60 : 10;
    switch (e.key) {
      case " ":
        e.preventDefault();
        this.player.toggle();
        break;
      case "ArrowLeft":
        e.preventDefault();
        this.player.step(-big);
        break;
      case "ArrowRight":
        e.preventDefault();
        this.player.step(big);
        break;
      case "f":
      case "а":
        toggleFullscreen(this.stage);
        break;
    }
  }
}
